import {
  Button,
  Form,
  Message,
  useToaster,
  Stack,
  Nav,
  TagPicker,
  SelectPicker,
  Toggle,
  Input,
  Loader,
  Radio,
  RadioGroup,
  Dropdown,
} from "rsuite";
import "./_upsert-app.scss";
import CloseIcon from "@rsuite/icons/Close";
import SaveIcon from "@rsuite/icons/Save";
import CopyIcon from "@rsuite/icons/Copy";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';

import { useEffect, useState, useRef } from "react";
import React from "react";
import {
  EPlatform,
  IApp,
  IDomain,
  // IntegrationVersion, // Removed - field no longer exists
  IPlatformParams,
  IUpsertAppRequest,
} from "@bprotsyk/aso-core";
import { AppInterface } from "ui/containers/app/interface/app-interface";
import { AppJSON } from "./json/app-json";
import { countryNames, getCountryLabel } from "../../../../constants/countries";
import PlatformsAccordion from "./platforms";
import { emptyAppState } from "constants/app-initial";
import { IUpsertStubRequest, PanelService } from "network/panel/network";
// import { IAppStubParams, StubParams } from "../app-stub/stub-params"; // Removed - offersStubParams no longer exists

interface IProps {
  onClose: () => void;
  app?: IApp;
  create?: boolean;
  selectedPlatform?: EPlatform | null;
  selectedIntegration?: string | null;
}

const NO_DOMAINS_OPTION = [
  {
    label: "Немає доступних доменів",
    value: "no_domains",
  },
];

interface INoDomainsOption {
  label: string;
  value: string;
}

const PASSWORD = "123456";

type TabKey = "general" | "json";

const placement = "topEnd";

// Extend IPlatformParams to include newPlatform
interface ExtendedPlatformParams extends IPlatformParams {
  newPlatform?: boolean;
}

const RadioLabel = ({ children }: { children: React.ReactNode }) => (
  <label style={{ padding: 7 }}>{children}</label>
);

export default function UpsertAppModal({
  onClose,
  app,
  create,
  selectedPlatform,
  selectedIntegration,
}: IProps) {
  const toaster = useToaster();
  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    if (selectedIntegration === "settings") {
      return "json";
    }
    return (selectedIntegration as TabKey) || "general";
  });
  const [appStub, setAppStub] = useState<IUpsertStubRequest | null>(null);
  const [appState, setAppState] = useState<IApp | IUpsertAppRequest>(
    app || emptyAppState
  );

  const [isCreating, setIsCreating] = useState(create || false);
  const [isSaving, setIsSaving] = useState(false);
  console.log(activeTab, selectedIntegration);

  // Track if app has been modified
  const [hasChanges, setHasChanges] = useState(false);
  const [originalApp, setOriginalApp] = useState<IApp | IUpsertAppRequest | null>(null);
  
  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{ name?: boolean; bundle?: boolean }>({});

  // Custom dropdown states
  const [isIntegrationDropdownOpen, setIsIntegrationDropdownOpen] = useState(false);
  const [integrationDropdownPosition, setIntegrationDropdownPosition] = useState<{ x: number; y: number } | null>(null);
  const integrationDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (integrationDropdownRef.current && !integrationDropdownRef.current.contains(event.target as Node)) {
        setIsIntegrationDropdownOpen(false);
      }
    };

    if (isIntegrationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isIntegrationDropdownOpen]);

  // Close modal on ESC if no changes
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !hasChanges) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [hasChanges, onClose]);

  useEffect(() => {
    if (app?.platforms === null) {
      setAppState({ ...app, platforms: {} } as IUpsertAppRequest);
    }
  }, [app]);

  // Initialize original app state for change detection
  useEffect(() => {
    if (app) {
      setOriginalApp(JSON.parse(JSON.stringify(app)));
    } else if (create) {
      setOriginalApp(JSON.parse(JSON.stringify(emptyAppState)));
    }
  }, [app, create]);

  // Check for changes whenever appState changes
  useEffect(() => {
    if (originalApp && appState) {
      const cleanedCurrentState = {
        ...appState,
        platforms: cleanPlatformsData(appState.platforms),
      };
      const cleanedOriginalState = {
        ...originalApp,
        platforms: cleanPlatformsData(originalApp.platforms),
      };
      const hasAppChanged = JSON.stringify(cleanedOriginalState) !== JSON.stringify(cleanedCurrentState);
      setHasChanges(hasAppChanged);
    }
  }, [appState, originalApp]);

  const availableCountries = Object.keys(countryNames)
    .map((code) => ({
      label: getCountryLabel(code),
      value: code,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const [isEditingAllowed, setIsEditingAllowed] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [availableDomain, setAvailableDomain] = useState<
    IDomain[] | INoDomainsOption[]
  >(NO_DOMAINS_OPTION);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  // const [stubData, setStubData] = useState<IAppStubParams | null>(null); // offersStubParams removed

  const handleCountriesChange = (value: string[]) => {
    setAppState((prevState: IApp | IUpsertAppRequest) =>
      prevState
        ? ({
            ...prevState,
            // geos field removed
          } as IApp | IUpsertAppRequest)
        : prevState
    );
  };

  useEffect(() => {
    const filter = `/?target=main&appid=false`;
    PanelService.getDomains(filter).then((res) => {
      if (res.length > 0) {
        setAvailableDomain(res);
      }
    });
  }, []);

  const domainOptions = availableDomain?.flatMap((domain) => {
    if ("name" in domain && "id" in domain) {
      return {
        label: domain.name,
        value: String(domain.id),
      };
    }
    return NO_DOMAINS_OPTION;
  });

  const cleanPlatformsData = (
    platforms: Record<string, ExtendedPlatformParams>
  ) => {
    const cleanedPlatforms: Record<string, IPlatformParams> = {};
    Object?.keys(platforms).forEach((key) => {
      const { newPlatform, ...rest } = platforms[key];
      cleanedPlatforms[key] = rest;
    });
    return cleanedPlatforms;
  };

  const validateForm = () => {
    const errors: { name?: boolean; bundle?: boolean } = {};
    
    if (!appState.name || appState.name.trim() === '') {
      errors.name = true;
    }
    
    if (!appState.bundle || appState.bundle.trim() === '') {
      errors.bundle = true;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    // Validate before saving
    if (!validateForm()) {
      toaster.push(
        message("error", "Будь ласка, заповніть всі обов'язкові поля (Назва та Bundle)"),
        { placement, duration: 5000 }
      );
      return;
    }
    
    setIsSaving(true);
    const savePromise = new Promise(async (resolve, reject) => {
      try {
        const cleanedAppState = {
          ...appState,
          enabled: true, // Always enabled
          platforms: cleanPlatformsData(appState.platforms),
        } as IUpsertAppRequest;
        const appResponse = await PanelService.upsertApp(cleanedAppState);
        setAppState(appResponse.data);
        
        if(appStub){
          const stubResponse = await PanelService.upsertStub(appStub);
          // setStubData(stubResponse.data); // stubData removed
        }
        resolve(appResponse.data);
      } catch (error) {
        reject(error);
      }
    });

    savePromise
      .then((data: any) => {
        toaster.push(
          message("success", "Додаток успішно збережено"),
          { placement, duration: 5000 }
        );
        onClose();
      })
      .catch((error) => {
        console.error("Помилка при збереженні додатку:", error);
        toaster.push(
          message("error", "Помилка при збереженні додатку"),
          { placement, duration: 5000 }
        );
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  // handleVersionChange removed - integrationVersion field no longer exists

  const handlePasswordSubmit = () => {
    if (password === PASSWORD) {
      setIsEditingAllowed(true);
      setShowPasswordModal(false);
      setPassword("");
    } else {
      toaster.push(
        message("error", "Невірний пароль"),
        { placement, duration: 3000 }
      );
    }
  };

  const handleEditAttempt = () => {
    if (!isEditingAllowed) {
      setShowPasswordModal(true);
    }
  };

  const message = (messageType: any, messageText: string) => {
    return (
      <Message key={new Date().getTime()} showIcon type={messageType} closable>
        <strong>{messageText}</strong>
      </Message>
    );
  };

  const handlePlatformDataChange = (
    updatedPlatforms: Record<string, IPlatformParams>
  ) => {
    setAppState((prevState: IApp | IUpsertAppRequest) =>
      prevState
        ? ({
            ...prevState,
            platforms: updatedPlatforms,
          } as IApp | IUpsertAppRequest)
        : prevState
    );
  };

  const handleDomainNameChange = (value: string) => {
    setAppState((prevState: IApp | IUpsertAppRequest) =>
      prevState
        ? ({
            ...prevState,
            domainParams: {
              ...prevState.domainParams,
              name: value,
            },
          } as IApp | IUpsertAppRequest)
        : prevState
    );
  };

  const handleIdChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      setAppState((prevState: IApp | IUpsertAppRequest) =>
        prevState
          ? ({
              ...prevState,
              id: numValue,
            } as IApp | IUpsertAppRequest)
          : prevState
      );
    }
  };

  const handleBundleChange = (value: string) => {
    setAppState((prevState: IApp | IUpsertAppRequest) =>
      prevState
        ? ({
            ...prevState,
            bundle: value,
          } as IApp | IUpsertAppRequest)
        : prevState
    );
  };

  const handleNameChange = (value: string) => {
    setAppState((prevState: IApp | IUpsertAppRequest) =>
      prevState
        ? ({
            ...prevState,
            name: value,
          } as IApp | IUpsertAppRequest)
        : prevState
    );
  };

  const updateApp = (updatedApp: IApp | IUpsertAppRequest) => {
    setAppState(updatedApp);
  };

  const handleDomainChange = (value: string | null) => {
    setSelectedDomain(value);
    setAppState((prevState) => ({
      ...prevState,
      domainParams: {
        ...prevState.domainParams,
        name:
          domainOptions.find((option) => option?.value === value)?.label || "",
      },
    } as IUpsertAppRequest));
  };

  const handleSaveStub = (params: IUpsertStubRequest) => {
     setAppState((prevState: IApp | IUpsertAppRequest) =>
      prevState
        ? ({
            ...prevState,
            // offersStubParams: params.offersStubParams, // Removed - field no longer exists
          } as IApp | IUpsertAppRequest)
        : prevState
    );
   setAppStub(params)
  };

  const copyTrackingParams = () => {
    const directPlatform = appState.platforms?.["@"]?.direct;
    if (directPlatform?.keitaroData?.trackingParams) {
      const { naming, advertising_id, appsflyer_device_id } = directPlatform.keitaroData.trackingParams;
      const tabSeparatedValues = `${naming || ''}\t${appsflyer_device_id || ''}\t${advertising_id || ''}`;
      
      navigator.clipboard.writeText(tabSeparatedValues).then(() => {
        toaster.push(
          message("success", "Параметри трекінгу скопійовано"),
          { placement, duration: 3000 }
        );
      }).catch(() => {
        toaster.push(
          message("error", "Помилка копіювання"),
          { placement, duration: 3000 }
        );
      });
    }
  };

  // Integration dropdown handlers removed - integrationVersion field no longer exists

  const renderTabContent = () => {
    switch (activeTab) {
      case "general":
        return (
          <div className="content">
            <div className="general-layout">
              <div className="general-container-left">
                {/* Integration version dropdown removed - field no longer exists */}

                {create && (
                  <div className="id-container">
                    <label>ID</label>
                    <Input
                      value={appState.id === 0 ? "" : String(appState.id)}
                      onChange={(value) => {
                        if (value === "") {
                          handleIdChange("0");
                        } else {
                          handleIdChange(value);
                        }
                      }}
                      placeholder="Введіть ID додатку"
                      style={{ width: "100%" }}
                    />
                  </div>
                )}

                {/* General Title */}
                <div className="field-group general-title-group" style={{ padding: 0, margin: '16px 0 8px 0' }}>
                  <div className="group-header" style={{ padding: 0, display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '3px', height: '20px', backgroundColor: '#27ae60', marginRight: '8px' }}></div>
                    <h3 style={{ textTransform: 'none', color: '#ffffff', margin: 0, fontSize: '16px' }}>Головне</h3>
                  </div>
                </div>

                <div className="domain-name-container">
                  {appState.domainParams.name && !create ? (
                    <div style={{ marginBottom: '16px' }}>
                      <label>Домен</label>
                      <div 
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          marginTop: '8px'
                        }}
                      >
                        <div 
                          style={{
                            color: '#3498db',
                            textDecoration: 'underline',
                            fontFamily: 'monospace',
                            fontSize: '14px',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={() => navigator.clipboard.writeText(appState.domainParams.name)}
                        >
                          {appState.domainParams.name}
                        </div>
                        <FontAwesomeIcon 
                          icon={faCopy} 
                          style={{ color: '#3498db', fontSize: '12px', cursor: 'pointer' }} 
                          onClick={() => navigator.clipboard.writeText(appState.domainParams.name)}
                        />
                      </div>
                    </div>
                  ) : create ? (
                    <>
                      <label>Назва домену</label>
                      <SelectPicker
                        data={domainOptions}
                        value={selectedDomain}
                        onChange={handleDomainChange}
                        onOpen={() => {
                          setTimeout(() => {
                            const searchInput = document.querySelector('.rs-search-box-input') as HTMLInputElement;
                            if (searchInput) {
                              searchInput.focus();
                            }
                          }, 100);
                        }}
                        searchable
                        placeholder="Оберіть домен"
                        locale={{ searchPlaceholder: 'Пошук' }}
                        menuMaxHeight={400}
                        disabled={selectedDomain === NO_DOMAINS_OPTION[0].value}
                        style={{ width: "100%" }}
                        container={() => document.body}
                        placement="autoVerticalStart"
                        menuStyle={{ zIndex: 10000 }}
                      />
                    </>
                  ) : null}
                </div>

                <div className="bundle-container">
                  <label>Bundle {validationErrors.bundle && <span style={{ color: '#ff5722' }}>*</span>}</label>
                  <Input
                    value={appState.bundle || ""}
                    onChange={(value) => {
                      handleBundleChange(value);
                      if (validationErrors.bundle) {
                        setValidationErrors(prev => ({ ...prev, bundle: false }));
                      }
                    }}
                    style={{ 
                      width: "100%",
                      borderColor: validationErrors.bundle ? '#ff5722' : undefined
                    }}
                  />
                  {validationErrors.bundle && (
                    <span style={{ color: '#ff5722', fontSize: '12px', marginTop: '4px' }}>
                      Bundle є обов'язковим полем
                    </span>
                  )}
                </div>

                <div className="bundle-container">
                  <label>Назва {validationErrors.name && <span style={{ color: '#ff5722' }}>*</span>}</label>
                  <Input
                    value={appState.name || ""}
                    onChange={(value) => {
                      handleNameChange(value);
                      if (validationErrors.name) {
                        setValidationErrors(prev => ({ ...prev, name: false }));
                      }
                    }}
                    style={{ 
                      width: "100%",
                      borderColor: validationErrors.name ? '#ff5722' : undefined
                    }}
                  />
                  {validationErrors.name && (
                    <span style={{ color: '#ff5722', fontSize: '12px', marginTop: '4px' }}>
                      Назва є обов'язковим полем
                    </span>
                  )}
                </div>

                {appState.platforms?.["@"]?.direct?.enabled && 
                 appState.platforms?.["@"]?.direct?.keitaroData?.trackingParams && (
                  <div className="tracking-params-container" style={{ display: 'none' }}>
                    <label style={{ fontSize: '12px', color: '#8e8e93', marginBottom: '8px', display: 'block' }}>Параметри трекінгу</label>
                    <div className="tracking-params-block" style={{ 
                      display: 'flex', 
                      gap: '12px',
                      alignItems: 'center',
                      backgroundColor: 'rgb(36 40 48)',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid rgb(61 63 67)',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        flex: 1
                      }}>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <div style={{ minWidth: '80px' }}>
                            <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Naming:</div>
                            <div style={{ 
                              padding: '4px 8px', 
                              backgroundColor: '#f3f4f6', 
                              borderRadius: '4px',
                              color: '#374151',
                              border: '1px solid rgb(61 63 67'
                            }}>
                              {appState.platforms["@"].direct.keitaroData.trackingParams.naming || 'не вказано'}
                            </div>
                          </div>
                          <div style={{ minWidth: '80px' }}>
                            <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Device ID:</div>
                            <div style={{ 
                              padding: '4px 8px', 
                              backgroundColor: '#f3f4f6', 
                              borderRadius: '4px',
                              color: '#374151',
                              border: '1px solid #e5e7eb'
                            }}>
                              {appState.platforms["@"].direct.keitaroData.trackingParams.appsflyer_device_id || 'не вказано'}
                            </div>
                          </div>
                          <div style={{ minWidth: '80px' }}>
                            <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Advertising ID:</div>
                            <div style={{ 
                              padding: '4px 8px', 
                              backgroundColor: '#f3f4f6', 
                              borderRadius: '4px',
                              color: '#374151',
                              border: '1px solid #e5e7eb'
                            }}>
                              {appState.platforms["@"].direct.keitaroData.trackingParams.advertising_id || 'не вказано'}
                            </div>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={copyTrackingParams}
                        style={{
                          background: '#3b82f6',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#ffffff',
                          fontSize: '12px',
                          fontWeight: '500',
                          gap: '6px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
                        title="Копіювати всі параметри (розділені табами для вставки в Google Sheets)"
                      >
                        <CopyIcon />
                        Копіювати
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Platforms settings merged from settings tab */}
              <div className="general-container-platforms">
              {appState && (
                <PlatformsAccordion
                  platforms={appState.platforms}
                  app={appState as IApp}
                  onPlatformDataChange={handlePlatformDataChange}
                  selectedPlatform={selectedPlatform}
                />
              )}
              </div>
            </div>
          </div>
        );
      // Settings tab removed - merged into general tab
      case "json":
        return appState ? (
          <div onClick={handleEditAttempt}>
            <AppJSON
              app={appState}
              isEditable={isEditingAllowed}
              updateApp={updateApp}
            />
          </div>
        ) : null;

      // Stub params case removed - integrationVersion no longer exists
      default:
        return null;
    }
  };

  // Integration version functions removed - field no longer exists

  return (
    <div className="upsert-app-backdrop" onClick={(e) => {
      // Close modal if clicking on backdrop (not on modal content)
      if (e.target === e.currentTarget && !hasChanges) {
        onClose();
      }
    }}>
      <div className="upsert-app-container" onClick={(e) => e.stopPropagation()}>
        <div className="upsert-app-header">
        <div className="upsert-app-header-content">
          <div className="upsert-app-id-section">
            <span 
              className="upsert-app-id" 
              style={{ 
                color: appState.id === 0 ? '#ff5722' : '#169de0',
                fontSize: appState.id === 0 ? 'inherit' : '24px',
                fontWeight: appState.id === 0 ? 'inherit' : 'bold'
              }}
            >
              {appState.id === 0 ? 'Оберіть ID' : `#${appState.id}`}
            </span>
          </div>
          <div className="upsert-app-actions">
            <RadioGroup 
              name="tabGroup" 
              className="tabGroup" 
              onChange={value => setActiveTab(value as TabKey)} 
              inline 
              appearance="picker" 
              value={activeTab}
            >
              <Radio value="general">Загальне</Radio>
              <Radio value="json">JSON</Radio>
            </RadioGroup>
          </div>
          <div className="upsert-app-search">
            <div className="upsert-app-buttons">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                className="lists-tab save-btn"
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
              >
                <SaveIcon /> {isSaving ? 'Збереження...' : 'Зберегти'}
              </button>
              </div>
              <span className="upsert-app-separator" />
              <button
                className="lists-tab close-btn"
                onClick={onClose}
              >
                <CloseIcon /> Закрити
              </button>
            </div>
          </div>
        </div>
      </div>

        <div className="upsert-app-content">
          {renderTabContent()}
        </div>

        {/* Integration dropdown removed - field no longer exists */}
      </div>
    </div>
  );
}
